import _extends from '@babel/runtime/helpers/extends';
import React__default, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { CSSTransition } from 'react-transition-group';
import Card from '../Card/Card.js';
import Button from '../Button/Button.js';
import Icon from '../Icon/Icon.js';
import Text from '../Text/Text.js';
import { isNumber } from '../../utils/objects.js';

const ICON_MAP = {
  success: 'check',
  danger: 'error-white',
  warning: 'warning-white',
  neutral: 'info'
};
const ALERT_PROPS = {
  intent: ['danger', 'success', 'warning', 'neutral']
};
const TEXT_COLOR = 'foreground-secondary';
const TIMEOUT_INTERVAL_MS = 1000;
const alertPropTypes = {
  /**
   * determines the visual appearance of alert
   */
  intent: PropTypes.oneOf(ALERT_PROPS.intent),
  /**
   * primary message of alert
   */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  /**
   * additional message rendered below the title
   */
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /**
   * callback run after alert is dismissed
   */
  onDismiss: PropTypes.func.isRequired,
  /**
   * time in ms before alert automatically disappears
   */
  timeout: PropTypes.number,
  /**
   * whether the alert should transition in / out of view
   */
  withTransition: PropTypes.bool,
  /**
   * whether the timeout should pause when the mouse is over the alert or when the alert is focused by the keyboard
   */
  shouldPauseTimeoutOnHover: PropTypes.bool
};
// @TODO Should there be an optional "name" prop that would be used for data-testid?
const Alert = ({
  title,
  content,
  containerProps,
  onDismiss,
  intent = 'neutral',
  timeout = 5000,
  withTransition = true,
  shouldPauseTimeoutOnHover = true
}) => {
  const baseClass = 'nui-alert';
  const [shouldDismiss, setShouldDismiss] = useState(false);
  const [didTimeoutExpire, setDidTimeoutExpire] = useState(false);
  const [timeLeftMs, setTimeLeftMs] = useState(timeout);
  const [isTimerPaused, setIsTimerPaused] = useState(false);
  const intervalIdRef = useRef();
  const cardElement = useRef(null);
  useEffect(() => {
    if (timeout <= 0 || !isNumber(timeout)) return undefined;
    const intervalId = setInterval(() => {
      if (!isTimerPaused || !shouldPauseTimeoutOnHover) {
        setTimeLeftMs(prevState => prevState - TIMEOUT_INTERVAL_MS);
      }
    }, TIMEOUT_INTERVAL_MS);
    intervalIdRef.current = intervalId;
    return () => {
      clearInterval(intervalId);
    };
  }, [timeLeftMs, isTimerPaused]);
  useEffect(() => {
    if (isNumber(timeout) && timeout > 0 && timeLeftMs <= 0) {
      setShouldDismiss(true);
      setDidTimeoutExpire(true);
      clearInterval(intervalIdRef.current);
    }
  }, [timeLeftMs, timeout]);
  const handlePauseTimer = () => {
    if (shouldPauseTimeoutOnHover) {
      setIsTimerPaused(true);
    }
  };
  const handleResumeTimer = () => {
    if (shouldPauseTimeoutOnHover) {
      setIsTimerPaused(false);
    }
  };
  const renderCard = () => {
    const closeHandler = withTransition ? () => {
      setShouldDismiss(true);
    } : () => {
      onDismiss(didTimeoutExpire);
    };
    return /*#__PURE__*/React__default.createElement(Card, _extends({
      withShadow: true,
      tabIndex: 0
    }, containerProps, {
      className: cx(baseClass, containerProps?.className),
      intent: intent,
      onMouseEnter: handlePauseTimer,
      onMouseLeave: handleResumeTimer,
      onFocus: handlePauseTimer,
      onBlur: handleResumeTimer,
      ref: cardElement
    }), /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("div", {
      className: "nui-alert_intent-icon"
    }, /*#__PURE__*/React__default.createElement(Icon, {
      fill: "white",
      name: ICON_MAP[intent],
      title: intent
    })), /*#__PURE__*/React__default.createElement("div", {
      className: "nui-alert_spacer"
    })), /*#__PURE__*/React__default.createElement("div", {
      className: "nui-alert_main"
    }, /*#__PURE__*/React__default.createElement("div", {
      className: "nui-alert_main-title"
    }, typeof title === 'string' ? /*#__PURE__*/React__default.createElement(Text, {
      element: "p",
      color: TEXT_COLOR
    }, title) : title), content && /*#__PURE__*/React__default.createElement("div", {
      className: "nui-alert_main-content"
    }, typeof content === 'string' ? /*#__PURE__*/React__default.createElement(Text, {
      element: "p",
      size: "xsmall",
      color: TEXT_COLOR
    }, content) : content)), /*#__PURE__*/React__default.createElement("div", {
      className: "nui-alert_spacer"
    }), /*#__PURE__*/React__default.createElement("div", {
      className: "nui-alert_close"
    }, /*#__PURE__*/React__default.createElement(Button, {
      iconOnly: true,
      variant: "default",
      iconProps: {
        name: 'close',
        title: 'Close',
        fill: 'dark-gray'
      },
      onClick: closeHandler,
      size: "small"
    }, "Close")));
  };
  return withTransition ? /*#__PURE__*/React__default.createElement(CSSTransition, {
    nodeRef: cardElement,
    onExited: () => {
      onDismiss(didTimeoutExpire);
    },
    classNames: "nui-alert",
    appear: true,
    in: !shouldDismiss,
    timeout: 300
  }, renderCard()) : renderCard();
};
Alert.propTypes = alertPropTypes;
Alert.displayName = 'NuiAlert';
var Alert$1 = Alert;

export { ALERT_PROPS, Alert$1 as default };
